import { useState } from 'react';
import type { Claim, ClaimItem, CreateClaimPayload } from '../types/LegalCase';

interface UseLegalCaseClaimFormArgs {
    createClaim: (data: CreateClaimPayload) => Promise<boolean>;
    updateClaim: (data: CreateClaimPayload) => Promise<boolean>;
    clearErrors: () => void;
}

// Estado e handlers dos forms da pretensão: criação, **edição dos valores** (AF-3691) e o
// toggle/seleção do form de título. Extraído do componente (diretriz do repo: sem função dentro
// de função; padrão do useLegalCasePartyForm da Task 3). Centraliza o reset dos campos:
// abrir/cancelar/salvar com sucesso sempre zera, então valores digitados numa tentativa
// anterior não vazam para a próxima.
export default function useLegalCaseClaimForm({
    createClaim,
    updateClaim,
    clearErrors,
}: UseLegalCaseClaimFormArgs) {
    const [showItemForm, setShowItemForm] = useState(false);
    // Título em edição (null = nenhum). Abrir a edição de um título fecha o form de adição e
    // vice-versa: um form de título por vez evita dois estados concorrentes na mesma seção.
    const [editingItem, setEditingItem] = useState<ClaimItem | null>(null);
    const [creating, setCreating] = useState(false);
    const [editing, setEditing] = useState(false);
    const [principal, setPrincipal] = useState(0);
    const [interest, setInterest] = useState(0);
    const [penalties, setPenalties] = useState(0);
    const [description, setDescription] = useState('');

    const resetFields = () => {
        setPrincipal(0);
        setInterest(0);
        setPenalties(0);
        setDescription('');
    };

    const openCreate = () => {
        clearErrors();
        resetFields();
        setCreating(true);
    };

    const cancelCreate = () => {
        clearErrors();
        resetFields();
        setCreating(false);
    };

    const submitCreate = async () => {
        const ok = await createClaim(
            buildPayload({ principal, interest, penalties, description }, 'create'),
        );
        if (ok) {
            resetFields();
            setCreating(false);
        }
    };

    // Edição dos valores da pretensão: semeia os campos com o que está persistido. O PUT do
    // backend é full-replace, então zerar um campo aqui e salvar **limpa** o valor de fato — o
    // buildPayload em modo 'edit' manda `null` explícito para a chave chegar ao PUT.
    const openEdit = (claim: Claim) => {
        clearErrors();
        // O valor DIGITADO, não o efetivo: com títulos, `principal` é a soma deles (calculada). Se o
        // form carregasse o efetivo, salvar gravaria a soma como se fosse digitada. Fallback para
        // `principal` cobre resposta anterior à regra (sem `principalEntered`).
        setPrincipal(claim.principalEntered ?? claim.principal ?? 0);
        setInterest(claim.interest ?? 0);
        setPenalties(claim.penalties ?? 0);
        setDescription(claim.description ?? '');
        setEditing(true);
    };

    const cancelEdit = () => {
        clearErrors();
        resetFields();
        setEditing(false);
    };

    const submitEdit = async () => {
        const ok = await updateClaim(
            buildPayload({ principal, interest, penalties, description }, 'edit'),
        );
        if (ok) {
            resetFields();
            setEditing(false);
        }
    };

    const openItemForm = () => {
        clearErrors();
        setEditingItem(null);
        setShowItemForm(true);
    };

    const closeItemForm = () => {
        clearErrors();
        setEditingItem(null);
        setShowItemForm(false);
    };

    const openItemEdit = (item: ClaimItem) => {
        clearErrors();
        setShowItemForm(false);
        setEditingItem(item);
    };

    return {
        // form de criação / edição dos valores
        creating, openCreate, cancelCreate, submitCreate,
        editing, openEdit, cancelEdit, submitEdit,
        principal, setPrincipal,
        interest, setInterest,
        penalties, setPenalties,
        description, setDescription,
        // form de título (adição e edição)
        showItemForm, openItemForm, closeItemForm,
        editingItem, openItemEdit,
    };
}

interface ClaimValues {
    principal: number;
    interest: number;
    penalties: number;
    description: string;
}

// Extraída para fora do hook (evita função dentro de função — diretriz de qualidade do repo).
// Monta o payload com semântica diferente por modo, porque o UpdateClaim do backend é
// full-replace (o que não chega vira None):
// - **criação** (`mode='create'`): valor zerado/vazio → `undefined`, a chave é omitida (não faz
//   sentido criar uma pretensão com principal 0).
// - **edição** (`mode='edit'`): valor zerado/vazio → `null` **explícito**, que o backend grava
//   como "sem valor" — é o que permite o operador **limpar** um principal/juros/multa/custa/obs
//   que existia. Com `undefined` o JSON.stringify omitiria a chave e a limpeza nunca chegaria ao
//   PUT (apontamento do Copilot).
//
// AF-3991: `legalCosts` saiu do payload — custas virou somente leitura (soma das despesas do caso
// marcadas "cobrar na pretensão", calculada pelo backend). A tela não envia mais custas.
function buildPayload(
    { principal, interest, penalties, description }: ClaimValues,
    mode: 'create' | 'edit',
): CreateClaimPayload {
    const empty = mode === 'edit' ? null : undefined;
    return {
        principal: principal > 0 ? principal : empty,
        interest: interest > 0 ? interest : empty,
        penalties: penalties > 0 ? penalties : empty,
        description: description.trim() || empty,
    };
}
